import React from "react";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
} from "lucide-react";

export const statusStyles: Record<string, string> = {
  SUCCESS: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  FAILED: "bg-red-500/10 text-red-400 border-red-500/30",
  FAILURE: "bg-red-500/10 text-red-400 border-red-500/30",
  PARTIAL_SUCCESS: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  WARN: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  SKIPPED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  PENDING: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  CANCELLED: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  CREATED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
};

export const statusIcon = (status: string) => {
  if (status === "SUCCESS" || status === "CREATED") return <CheckCircle2 size={13} />;
  if (status === "FAILED" || status === "FAILURE") return <XCircle size={13} />;
  if (status === "WARN" || status === "PARTIAL_SUCCESS") return <AlertTriangle size={13} />;
  return <Info size={13} />;
};

export const actionStyles: Record<string, string> = {
  CREATE: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  UPDATE: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  DELETE: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  LOGIN: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  LOGOUT: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  APPROVE: "bg-violet-500/10 text-violet-400 border-violet-500/30",
  ACCESS_DENIED: "bg-red-500/10 text-red-400 border-red-500/30",
  API_ACCESS: "bg-blue-500/10 text-blue-400 border-blue-500/30",
};

export const auditCategoryStyles: Record<string, string> = {
  AUTH: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  SECURITY: "bg-red-500/10 text-red-400 border-red-500/30",
  PAYMENT: "bg-violet-500/10 text-violet-400 border-violet-500/30",
  API: "bg-blue-500/10 text-blue-400 border-blue-500/30",
  ADMIN: "bg-slate-400/10 text-slate-300 border-slate-500/30",
  PROPERTY: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  SYSTEM: "bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30",
};

export const actorTypeStyles: Record<string, string> = {
  ADMIN: "bg-violet-500/10 text-violet-400 border-violet-500/30",
  "SUPER_ADMIN": "bg-violet-500/10 text-violet-400 border-violet-500/30",
  USER: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  RECRUITER: "bg-teal-500/10 text-teal-400 border-teal-500/30",
  SYSTEM: "bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30",
};

const eventTypePrefixStyles: Array<[string, string]> = [
  ["PAYMENT", "bg-violet-500/10 text-violet-400 border-violet-500/30"],
  ["EMAIL", "bg-sky-500/10 text-sky-400 border-sky-500/30"],
  ["USER_", "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"],
  ["PASSWORD", "bg-cyan-500/10 text-cyan-400 border-cyan-500/30"],
  ["INVITE", "bg-amber-500/10 text-amber-400 border-amber-500/30"],
  ["CANDIDATE", "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"],
  ["RECRUITER", "bg-teal-500/10 text-teal-400 border-teal-500/30"],
  ["COMPANY", "bg-indigo-500/10 text-indigo-400 border-indigo-500/30"],
  ["JOB", "bg-blue-500/10 text-blue-400 border-blue-500/30"],
  ["SUBSCRIPTION", "bg-fuchsia-500/10 text-fuchsia-400 border-fuchsia-500/30"],
  ["FEEDBACK", "bg-rose-500/10 text-rose-400 border-rose-500/30"],
  ["ADMIN_", "bg-slate-400/10 text-slate-300 border-slate-500/30"],
];

export const eventTypeStyle = (eventType?: string): string => {
  const key = (eventType || "").toUpperCase();
  for (const [prefix, style] of eventTypePrefixStyles) {
    if (key.startsWith(prefix)) return style;
  }
  return "bg-slate-500/10 text-slate-300 border-slate-500/30";
};

export const Badge: React.FC<{
  label: string;
  className?: string;
  icon?: React.ReactNode;
}> = ({ label, className, icon }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border whitespace-nowrap ${
      className || "bg-slate-500/10 text-slate-300 border-slate-500/30"
    }`}
  >
    {icon}
    {label}
  </span>
);